import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BUSINESSES, FLYER_DEFAULTS } from "@/data/gameData";
import { useGame } from "@/context/GameContext";
import FlyerPreview from "./FlyerPreview";
import { Sound } from "@/lib/sound";
import { Printer, Save } from "lucide-react";
import { toast } from "sonner";

export const defaultFlyer = (businessId, playerName, brandName) => ({
  headline: brandName || `${playerName}'s ${BUSINESSES[businessId].name} Service`,
  slogan: FLYER_DEFAULTS[businessId].slogan,
  services: [...FLYER_DEFAULTS[businessId].services],
  price: BUSINESSES[businessId].priceRange[0] + 2,
  contact: "",
  phone: "",
  showMascot: true,
  showTabs: true,
});

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</span>
    {children}
  </label>
);

const FlyerDesigner = ({ businessId, open, onClose }) => {
  const { state, saveFlyer, getBrand } = useGame();
  const biz = BUSINESSES[businessId];
  const brand = getBrand(businessId);
  const [d, setD] = useState(() => ({ ...defaultFlyer(businessId, state.playerName, brand.name), ...(state.flyers[businessId] || {}) }));
  const set = (patch) => setD((x) => ({ ...x, ...patch }));
  const inputCls = "mt-1 rounded-xl border-2 border-slate-300 font-bold";

  const handleSave = () => {
    saveFlyer(businessId, d);
    Sound.success();
    toast.success("Flyer saved!");
  };
  const handlePrint = () => {
    saveFlyer(businessId, d);
    Sound.whoosh();
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto rounded-3xl border-4 border-orange-300 bg-white p-5 sm:p-6" data-testid="flyer-designer-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
            🖨️ Flyer Designer · {biz.name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600 font-medium -mt-2">
          Design a real flyer, print it, and hand it out with a grown-up. Tip: a clear price and a friendly face get more calls!
        </p>
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="space-y-3">
            <Field label="Business name (headline)">
              <Input value={d.headline} maxLength={40} onChange={(e) => set({ headline: e.target.value })} className={inputCls} data-testid="flyer-headline-input" />
            </Field>
            <Field label="Slogan">
              <Input value={d.slogan} maxLength={50} onChange={(e) => set({ slogan: e.target.value })} className={inputCls} data-testid="flyer-slogan-input" />
            </Field>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">What I do (3 things)</span>
              {d.services.map((s, i) => (
                <Input
                  key={`service-${i}`}
                  value={s}
                  maxLength={36}
                  onChange={(e) => set({ services: d.services.map((x, j) => (j === i ? e.target.value : x)) })}
                  className={inputCls}
                  data-testid={`flyer-service-${i}`}
                />
              ))}
            </div>
            <Field label={`Starting price ($${biz.priceRange[0]}–$${biz.priceRange[1]} is fair)`}>
              <Input
                type="number"
                min={1}
                max={99}
                value={d.price}
                onChange={(e) => set({ price: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })}
                className={`${inputCls} max-w-[120px]`}
                data-testid="flyer-price-input"
              />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Contact line">
                <Input value={d.contact} maxLength={40} placeholder="Contact my parent" onChange={(e) => set({ contact: e.target.value })} className={inputCls} data-testid="flyer-contact-input" />
              </Field>
              <Field label="Parent's phone (ask first!)">
                <Input type="tel" value={d.phone} maxLength={20} placeholder="555-123-4567" onChange={(e) => set({ phone: e.target.value })} className={inputCls} data-testid="flyer-phone-input" />
              </Field>
            </div>
            <p className="text-[11px] font-bold text-rose-700 -mt-1">🛡️ Never put your own number or address — a grown-up's phone only, with their permission.</p>
            <div className="flex flex-wrap gap-5 pt-1">
              <label className="flex items-center gap-2 text-sm font-heading font-bold text-slate-700">
                <Switch checked={d.showMascot} onCheckedChange={(v) => set({ showMascot: v })} data-testid="flyer-mascot-toggle" /> Show mascot
              </label>
              <label className="flex items-center gap-2 text-sm font-heading font-bold text-slate-700">
                <Switch checked={d.showTabs} onCheckedChange={(v) => set({ showTabs: v })} data-testid="flyer-tabs-toggle" /> Tear-off tabs
              </label>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={handleSave} data-testid="flyer-save-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-emerald-600 py-5">
                <Save className="w-4 h-4 mr-1" /> Save flyer
              </Button>
              <Button onClick={handlePrint} data-testid="flyer-print-btn" className="bg-orange-500 hover:bg-orange-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-orange-600 py-5">
                <Printer className="w-4 h-4 mr-1" /> Print it!
              </Button>
            </div>
          </div>
          <div className="bg-slate-100 rounded-3xl p-4 flex items-start justify-center">
            <FlyerPreview businessId={businessId} design={d} avatar={state.avatar} playerName={state.playerName} brand={brand} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FlyerDesigner;
